import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Download, FileText, BookOpen } from "lucide-react";
import jsPDF from "jspdf";

function fmtSec(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function AdminDashboard() {
  const [students, setStudents] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [demo, setDemo] = useState(false);

  useEffect(() => {
    api.get("/admin/students").then(({ data }) => setStudents(data));
    api.get("/admin/settings").then(({ data }) => setDemo(data.demo_mode));
    fetch("/logo.jpg").then((r) => r.blob()).then((b) => {
      const reader = new FileReader();
      reader.onloadend = () => setLogoDataUrl(reader.result);
      reader.readAsDataURL(b);
    });
  }, []);

  const toggleDemo = async () => {
    const { data } = await api.post("/admin/settings/demo", { demo_mode: !demo });
    setDemo(data.demo_mode);
  };

  if (!students) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-500">Loading…</div>;

  const downloadTranscript = async (s) => {
    try {
      const { data } = await api.get(`/admin/students/${s.id}/transcript`);
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const W = 612;
      if (logoDataUrl) { try { doc.addImage(logoDataUrl, "JPEG", 60, 60, 60, 60); } catch { /* */ } }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(30, 64, 175);
      doc.text("CALIFORNIA CCW TRAINING — STUDENT TRANSCRIPT (DOJ AUDIT)", W / 2, 50, { align: "center" });
      doc.setLineWidth(1);
      doc.setDrawColor(15, 23, 42);
      doc.line(40, 60, W - 40, 60);

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(20);
      doc.text(data.student.name, 40, 95);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text(`Email: ${data.student.email}`, 40, 112);
      doc.text(`Track: ${data.student.track === "renewal" ? "Renewal (4-hour)" : "Initial (8-hour)"}`, 40, 126);
      doc.text(`Enrolled: ${new Date(data.student.created_at).toLocaleString()}`, 40, 140);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text("MODULE COMPLETION", 40, 175);
      doc.setLineWidth(0.5);
      doc.line(40, 180, W - 40, 180);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      let y = 200;
      const mult = data.student.track === "renewal" ? 0.5 : 1;
      let totalSec = 0;
      data.modules.forEach((m) => {
        const mp = data.progress?.modules?.[m.id] || {};
        const reqSec = Math.round(m.min_seconds * mult);
        totalSec += mp.seconds_spent || 0;
        const status = mp.quiz_passed ? "PASSED" : "INCOMPLETE";
        const score = mp.quiz_score != null ? `${Math.round(mp.quiz_score * 100)}%` : "—";
        doc.text(`Module ${m.order}. ${m.title}`, 40, y);
        doc.text(`Time: ${fmtSec(mp.seconds_spent || 0)} / ${fmtSec(reqSec)}`, 320, y);
        doc.text(`Quiz: ${score}`, 470, y);
        doc.setTextColor(mp.quiz_passed ? 21 : 153, mp.quiz_passed ? 128 : 27, mp.quiz_passed ? 61 : 27);
        doc.text(status, 540, y);
        doc.setTextColor(15, 23, 42);
        y += 18;
      });

      y += 10;
      doc.setLineWidth(0.5);
      doc.line(40, y, W - 40, y);
      y += 20;
      doc.setFont("helvetica", "bold");
      doc.text("FINAL EXAMINATION", 40, y);
      y += 18;
      doc.setFont("helvetica", "normal");
      const fp = data.progress?.final_exam || {};
      doc.text(`Score: ${fp.score != null ? Math.round(fp.score * 100) + "%" : "—"}`, 40, y);
      doc.text(`Attempts: ${fp.attempts || 0}`, 200, y);
      doc.text(`Status: ${fp.passed ? "PASSED" : "INCOMPLETE"}`, 320, y);
      y += 30;

      doc.setFont("helvetica", "bold");
      doc.text("TOTAL STUDY TIME LOGGED", 40, y);
      y += 18;
      doc.setFont("helvetica", "normal");
      doc.text(fmtSec(totalSec), 40, y);
      y += 30;

      if (data.certificate) {
        doc.setFont("helvetica", "bold");
        doc.text("ACKNOWLEDGEMENT", 40, y);
        y += 18;
        doc.setFont("helvetica", "normal");
        doc.text(`Issued: ${new Date(data.certificate.issued_at).toLocaleString()}`, 40, y);
        y += 14;
        doc.text(`Signed name: ${data.certificate.full_name}`, 40, y);
        try { doc.addImage(data.certificate.signature_data_url, "PNG", 40, y + 10, 180, 50); } catch { /* */ }
      }

      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated ${new Date().toLocaleString()} · For Department of Justice / Issuing Agency audit review`, W / 2, 770, { align: "center" });

      doc.save(`Transcript-${data.student.name.replace(/\s+/g, "_")}.pdf`);
    } catch {
      alert("Failed to load transcript.");
    }
  };

  const downloadCourseOutline = async () => {
    try {
      const { data: course } = await api.get("/course");
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const W = 612;
      const margin = 50;
      if (logoDataUrl) { try { doc.addImage(logoDataUrl, "JPEG", 60, 60, 60, 60); } catch { /* */ } }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(30, 64, 175);
      doc.text("CALIFORNIA CCW TRAINING — OFFICIAL COURSE OUTLINE", W / 2, 50, { align: "center" });
      doc.setDrawColor(15, 23, 42);
      doc.line(margin, 60, W - margin, 60);

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(18);
      doc.text(course.title, margin, 95);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text(`${course.subtitle} · Total ${Math.round(course.total_minutes / 60)} hours · Pass threshold ${Math.round(course.pass_threshold * 100)}%`, margin, 112);

      let y = 145;
      course.modules.forEach((m) => {
        if (y > 720) { doc.addPage(); y = 60; }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(15, 23, 42);
        doc.text(`Module ${m.order}. ${m.title}`, margin, y);
        y += 16;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.text(`Duration: ${m.duration_minutes} min · Min study: ${fmtSec(m.min_seconds)}`, margin, y);
        y += 14;
        doc.setTextColor(15, 23, 42);
        doc.setFontSize(10);
        const summary = doc.splitTextToSize(m.summary, W - margin * 2);
        doc.text(summary, margin, y);
        y += summary.length * 12 + 6;
        m.sections.forEach((s) => {
          if (y > 720) { doc.addPage(); y = 60; }
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.text(`• ${s.heading}`, margin + 10, y);
          y += 14;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(51, 65, 85);
          const body = doc.splitTextToSize(s.body, W - margin * 2 - 20);
          // Cap each section body at ~10 lines for outline brevity; full curriculum is the source of record.
          const capped = body.slice(0, 14);
          doc.text(capped, margin + 10, y);
          y += capped.length * 11 + 8;
          doc.setTextColor(15, 23, 42);
        });
        y += 6;
      });

      if (y > 700) { doc.addPage(); y = 60; }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(`Final Examination — ${course.final_exam_count} questions, ${Math.round(course.pass_threshold * 100)}% pass`, margin, y);

      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated ${new Date().toLocaleString()} · CA CCW Training Course Outline`, W / 2, 770, { align: "center" });

      doc.save(`CCW-Course-Outline.pdf`);
    } catch {
      alert("Failed to load curriculum.");
    }
  };

  const downloadCertFor = async (s) => {
    try {
      const { data: cert } = await api.get(`/admin/students/${s.id}/certificate`);
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const W = 612;
      if (logoDataUrl) { try { doc.addImage(logoDataUrl, "JPEG", 60, 60, 60, 60); } catch { /* */ } }
      doc.setDrawColor(15, 23, 42);
      doc.setLineWidth(2);
      doc.rect(36, 36, W - 72, 720);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(30, 64, 175);
      doc.text("CALIFORNIA · CONCEALED CARRY WEAPON TRAINING", W / 2, 100, { align: "center" });
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(26);
      doc.text("Acknowledgement of Completion", W / 2, 150, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text("8-Hour Online Training Course", W / 2, 175, { align: "center" });
      doc.setFontSize(22);
      doc.setFont("helvetica", "bold");
      doc.text(cert.full_name.toUpperCase(), W / 2, 320, { align: "center" });
      try { doc.addImage(cert.signature_data_url, "PNG", 80, 615, 200, 60); } catch { /* */ }
      doc.line(80, 680, 290, 680);
      doc.setFontSize(9);
      doc.text("STUDENT SIGNATURE", 80, 695);
      const dt = new Date(cert.issued_at);
      doc.setFontSize(11);
      doc.text(dt.toLocaleDateString(), 360, 670);
      doc.line(360, 680, W - 80, 680);
      doc.setFontSize(9);
      doc.text("DATE ISSUED", 360, 695);
      doc.save(`CCW-${cert.full_name.replace(/\s+/g, "_")}.pdf`);
    } catch (e) {
      alert("Failed to fetch certificate.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="border-b border-slate-200 pb-6 mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Administrator</div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mt-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            Student Roster
          </h1>
          <p className="text-slate-600 mt-2 text-sm">{students.length} enrolled student{students.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          <button data-testid="toggle-demo" onClick={toggleDemo}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm font-medium text-sm border ${demo ? "bg-amber-100 border-amber-400 text-amber-900" : "bg-white border-slate-300 text-slate-900 hover:bg-slate-50"}`}>
            Demo Mode: {demo ? "ON (10s/module)" : "OFF"}
          </button>
          <button data-testid="download-course-outline" onClick={downloadCourseOutline}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-900 hover:bg-slate-50 px-4 py-2.5 rounded-sm font-medium text-sm">
            <BookOpen className="h-4 w-4" /> Course Outline PDF
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
              <th className="px-4 py-3">Student</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Modules</th>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Final</th>
              <th className="px-4 py-3">Certificate</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {students.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No students yet.</td></tr>
            )}
            {students.map((s) => (
              <tr key={s.id} data-testid={`student-row-${s.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-4 font-medium text-slate-900">{s.name}</td>
                <td className="px-4 py-4 text-slate-700">{s.email}</td>
                <td className="px-4 py-4">
                  <span className="font-mono text-slate-700">{s.modules_passed} / {s.total_modules}</span>
                </td>
                <td className="px-4 py-4 font-mono text-slate-700">{fmtSec(s.total_seconds)}</td>
                <td className="px-4 py-4">
                  {s.final_exam_passed ? (
                    <span className="inline-flex items-center gap-1 text-green-700">
                      <CheckCircle2 className="h-4 w-4" /> {Math.round(s.final_score * 100)}%
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-slate-400">
                      <XCircle className="h-4 w-4" /> —
                    </span>
                  )}
                </td>
                <td className="px-4 py-4">
                  {s.has_certificate ? (
                    <span className="text-green-700 text-xs">Issued {new Date(s.certificate_issued_at).toLocaleDateString()}</span>
                  ) : (
                    <span className="text-slate-400 text-xs">Not issued</span>
                  )}
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="inline-flex items-center gap-3 justify-end">
                    <button
                      data-testid={`download-transcript-${s.id}`}
                      onClick={() => downloadTranscript(s)}
                      className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 text-sm"
                    >
                      <FileText className="h-4 w-4" /> Transcript
                    </button>
                    {s.has_certificate && (
                      <button
                        data-testid={`download-cert-${s.id}`}
                        onClick={() => downloadCertFor(s)}
                        className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 text-sm"
                      >
                        <Download className="h-4 w-4" /> Cert
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
